import { format, isAfter, isEqual, isValid, parse, startOfToday, isWithinInterval, differenceInDays } from 'date-fns';
import { isEmptyString } from './DisplayUtils';

const TODAY_DATE_IN_LOCAL_TIME = startOfToday();

/**
 * Checks whether the given date string equals to today's date or a future date.
 *
 * @param {string} dateString - The date in 'dd / MM / yyyy' format.
 * @returns {boolean} - True if the given date string is equal to today's date or a future date, and false otherwise.
 */
export const isCurrentOrFutureDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  const formatString = 'dd / MM / yyyy';
  const dateToCheck = parse(dateString, formatString, new Date());
  return isEqual(dateToCheck, TODAY_DATE_IN_LOCAL_TIME) || isAfter(dateToCheck, TODAY_DATE_IN_LOCAL_TIME);
};

/**
 * Check if a date is within the range.
 *
 * @param {string} date - date string in 'DD / MM / YYYY' format.
 * @param {object} dateFrom - date object (eg. new Date() or startOfToday()).
 * @param {object} dateTo - date object (eg. new Date() or startOfToday()).
 * @return {boolean} - true if date is within the range, and false otherwise.
 */
export const isDateInRange = (date, dateFrom, dateTo) => {
  if (!date || typeof date !== 'string') return false;
  const formatString = 'dd / MM / yyyy';
  const setDate = parse(date, formatString, new Date());

  try {
    return isWithinInterval(setDate, { start: dateFrom, end: dateTo });
  } catch (error) {
    logger.error(error);
    return false;
  }
};

/**
 * Function that takes a date string and tests if it can be converted to a valid date
 *
 * @param {string} dateString - Date string to test
 * @returns {boolean} - True if the string provided is a valid date string
 */
export const isDateFormat = (dateString) => {
  // Guard against null/undefined/empty/non-string values
  if (!dateString || typeof dateString !== 'string') return false;

  // Handle undefined value passing through
  if (!isEmptyString(dateString)) {
    // Make sure the date match the 'dd / mm / yyyy' format.
    if (dateString.length < 14) {
      return false;
    }
  }

  const formatString = 'dd / MM / yyyy';
  const dateToCheck = parse(dateString, formatString, new Date(), { locale: 'en-AU' });
  return isValid(dateToCheck);
};

/**
 * Removes all whitespaces from the given date string, e.g. 'dd / MM / yyyy' => 'dd/mm/yyyy'.
 *
 * @param {string} dateString - The date string got passed to be formatted.
 * @returns {string} - The date string with whitespace characters removed.
 */
export const removeWhitespacesFromDateString = (dateString) => {
  return dateString.replace(/\s/g, '');
};

/**
 * Converts the given 'dd / MM / yyyy' date string into the 'yyyy-MM-dd' format.
 *
 * @param {string} dateString - The date string got passed to be converted.
 * @returns {string | undefined} - The date string in yyyy-MM-dd format, or undefined if there was an error.
 */
export const convertDateToYYYYMMDD = (dateString) => {
  const formatString = 'dd / MM / yyyy';
  const dateToCheck = parse(dateString, formatString, new Date());
  try {
    // Wrapped in a try-catch in case the given date string does not represent a valid date.
    const convertedDate = format(dateToCheck, 'yyyy-MM-dd');
    return convertedDate;
  } catch (error) {
    return undefined;
  }
};

/**
 * Convert the given 'dd / MM / yyyy' or 'dd/MM/yyyy' date string into 'dd Month yyyy' format. (eg. 22 March 1991)
 *
 * @param {string} dateString - The date string got passed to be converted.
 * @returns - the date string in dd Month yyyy format, or undefined if there was an error.
 */
export const formatDateToDisplay = (dateString) => {
  if (!isEmptyString(dateString)) {
    try {
      const dateArray = dateString.split('/');
      // As month range in date object is from 0 to 11, will need to minus 1 to match
      const newDate = new Date(+dateArray[2].trim(), dateArray[1].trim() - 1, +dateArray[0].trim());
      const formatString = 'dd MMMM yyyy';
      const date = format(new Date(newDate), formatString);
      return date;
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
};

/**
 * Convert the given date string 'YYYY-MM-DD' to the format of 'DD/MM/YYYY' format.
 *
 * @param {string} dateString - The date string that needs to be converted.
 * @returns - The date string in the DD/MM/YYYY format, otherwise an undefined if there is an error.
 */
export const convertDateToDDMMYYYYWithSlash = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return undefined;
  const formatString = 'yyyy-MM-dd';
  const dateToCheck = parse(dateString, formatString, new Date());

  // Wrap the conversion in try-catch incase it fails if the dateString is not in the expected format.
  try {
    const convertedDate = format(dateToCheck, 'dd/MM/yyyy');
    return convertedDate;
  } catch (error) {
    logger.error(error);
    return undefined;
  }
};

/**
 * Checks the number of days between date given and current date is less than or equal to numOfDays given
 * @param {*} dateStringToCheck first date string in form 'YYYY-MM-DD'
 * @param {*} numOfDays number of days needed
 * @returns {boolean} true if date is within numOfDays of current date
 */
export const checkWithinXDays = (dateStringToCheck, numOfDays) => {
  if (dateStringToCheck !== '') {
    const dateToCheck = new Date(new Date(dateStringToCheck).setHours(0, 0, 0, 0));
    const dateToCheckAgainst = new Date(new Date().setHours(0, 0, 0, 0));

    // Calculating the no. of days between two dates
    const diffInDays = differenceInDays(dateToCheckAgainst, dateToCheck);

    return Math.abs(diffInDays) <= numOfDays;
  }

  return false;
};

/**
 * Checks the number of years between two dates is less than or equal to numOfYears given
 * @param {string} laterDateString
 * @param {string} earlierDateString
 * @param {number} numOfYears
 * @returns {boolean} true if difference of dates is within numOfYears
 */
export const checkWithinXYears = (laterDateString, earlierDateString, numOfYears) => {
  if (laterDateString && earlierDateString) {
    const laterDate = new Date(new Date(laterDateString).setHours(0, 0, 0, 0));
    const earlierDate = new Date(new Date(earlierDateString).setHours(0, 0, 0, 0));

    const diffInTime = (laterDate.getTime() - earlierDate.getTime()) / 1000;
    const diffInDays = diffInTime / (60 * 60 * 24);
    const diffInYears = diffInDays / 365.25;
    return Math.abs(diffInYears) <= numOfYears;
  }
  return false;
};

/**
 * Takes a string in 24 hour time format (i.e. HH:mm) and converts it to 12 hour time (i.e. HH:mm aa)
 *
 * @param {string} timeString - The 24 hour time string to be converted.
 * @returns A string representing the given time in 12 hour time.
 */
export const convert24HourTimeStringTo12HourTimeString = (timeString) => {
  const [hour, minutes] = timeString.split(':');
  let modifiedHour = hour;
  let period = '';

  // NOTE: `modifiedHour` is a string

  if (modifiedHour === '24') {
    modifiedHour = '12';
    period = 'AM';
  } else if (modifiedHour === '12') {
    period = 'PM';
  } else if (modifiedHour < 12) {
    period = 'AM';

    if (modifiedHour === '00') {
      modifiedHour = '12';
    } else if (modifiedHour.startsWith('0')) {
      // Remove the leading zero, e.g. '09' should become '9'
      modifiedHour = modifiedHour.substring(1);
    }
  } else if (modifiedHour > 12) {
    period = 'PM';
    modifiedHour = `${modifiedHour - 12}`;
  }
  return `${modifiedHour}:${minutes} ${period}`;
};

/**
 * Slices dateTime with format DD/MM/YYYY HH:mm:SS AM/PM or YYYY / MM / DD HH:mm:SS AM/PM into parts
 * - dateTimeParts: [DD, MM, YYYY] (dayfirst) or [YYYY, DD, MM]
 * - timeParts: [HH, mm, SS]
 * - amOrPm: 'AM' or 'PM'
 *
 * @param {string} dateTime dateTime String DD/MM/YYYY HH:mm:SS AM/PM or YYYY/MM/DD HH:mm:SS AM/PM
 * @param {boolean} dayFirst If true, it will format dateTimeParts to [DD, MM, YYYY]. If false, it will format dateTimeParts to [YYYY, MM, DD]
 * @returns {object} dateTimeParts Object
 */
export const sliceDateTimeToParts = (dateTime, dayFirst = false) => {
  let dateTimeParts = {};

  if (dayFirst) {
    dateTimeParts = {
      dateParts: dateTime.slice(0, 10).split('/'),
      timeParts: dateTime.slice(11, -3).split(':'),
      amOrPm: dateTime.slice(-2),
    };
  } else {
    dateTimeParts = {
      dateParts: dateTime.slice(0, 14).split(' / '),
      timeParts: dateTime.slice(15, -3).split(':'),
      amOrPm: dateTime.slice(-2),
    };
  }

  return dateTimeParts;
};

/**
 * Converts event date time to date object
 *
 * @param {string} eventDate - Event date time string in YYYY / MM / DD HH:mm:SS AM/PM
 * @param {boolean} [includeTime=false] - Includes time in event dateTime string if true to date object
 * @return {Date}
 */
export const convertEventDateToDateObj = (eventDate, includeTime = false) => {
  const dateTimeParts = sliceDateTimeToParts(eventDate);

  if (includeTime) {
    const hour = dateTimeParts.amOrPm === 'PM' ? +dateTimeParts.timeParts[0] + 12 : dateTimeParts.timeParts[0];

    const convertedDate = new Date(
      Date.UTC(
        dateTimeParts.dateParts[0], // Year
        dateTimeParts.dateParts[1] - 1, // Month (0-based)
        dateTimeParts.dateParts[2], // Day
        hour, // Hour
        dateTimeParts.timeParts[1], // Minutes
        dateTimeParts.timeParts[2], // Seconds
      ),
    );

    return convertedDate;
  }

  return new Date(Date.UTC(dateTimeParts.dateParts[0], +dateTimeParts.dateParts[1] - 1, dateTimeParts.dateParts[2]));
};

/**
 * Formats date from DD/MM/YYYY HH:mm:SS AM/PM to YYYY / MM / DD HH:mm AM/PM
 *
 * @param {string} eventDate - date in DD/MM/YYYY HH:mm:SS AM/PM
 * @returns formatted event date string in YYYY / MM / DD HH:mm AM/PM
 */
export const formatEventDate = (eventDate) => {
  if (eventDate) {
    const dateTimeParts = sliceDateTimeToParts(eventDate, true);
    return `${dateTimeParts.dateParts[2]} / ${dateTimeParts.dateParts[1]} / ${dateTimeParts.dateParts[0]} ${dateTimeParts.timeParts[0]}:${dateTimeParts.timeParts[1]} ${dateTimeParts.amOrPm}`;
  }
  return '';
};

/**
 * This function converts Credit Card MM / YY expiry date to a full yyyy-MM-dd date with date set to last day of the month.
 * @param {string} ccExpiry Credit card expiry as MM / YY
 * @returns formatted date string in yyyy-MM-dd format, where DD is last day fot he month
 */
export const convertCCExpiryToDate = (ccExpiry) => {
  const CURR_CENTURY = '20';
  const FORMAT = 'yyyy-MM-dd';

  if (!isEmptyString(ccExpiry)) {
    const [mth, year] = ccExpiry.split(' / ');
    const newDate = new Date(`${CURR_CENTURY}${year}`, Number(mth), 0); // Create date object. Don't need to subtract month index by 1 because we need the last day for that month, which is returned when day = 0
    return format(newDate, FORMAT);
  }

  return null;
};

/**
 * This function converts yyyy-MM-dd date to Credit Card expiry format (mm / yy)
 * @param {date} date Date to convert in yyyy-MM-dd format
 */
export const convertDateToCCExpiry = (date) => {
  if (!isEmptyString(date)) {
    const [y, m] = date.split('-');
    return `${m} / ${y.slice(2)}`;
  }
  return null;
};

/**
 * Parses date string with format YYYY-MM-DD to a date object
 * @param {date} date  
 * @returns {date} object
 */
export const parseFormDate = (date) => {
  const dateFormat = 'yyyy-MM-dd';
  return parse(date, dateFormat, new Date());
};
